import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getEmployeeTimeEntries,
  createEmployeeTimeEntry,
  deleteAdminTimeEntry,
  type TimeEntry,
} from '@/lib/api/portal';
import { getJobs, type Job } from '@/lib/api/jobs';
import { getEffectiveSchedule } from '@/lib/api/scheduling';
import { useToast } from '@/lib/hooks/useToast';

interface EmployeeTimesheetTabProps {
  employeeId: string | number;
}

interface LineDraft {
  job_id: string; // '' = no job
  description: string;
  hours: string;
}

const blankLine = (): LineDraft => ({ job_id: '', description: '', hours: '' });

// Worked hours from clock in/out minus break (handles crossing midnight).
const clockHours = (start: string, end: string, breakMin: number): number => {
  if (!start || !end) return 0;
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  let mins = toMin(end) - toMin(start);
  if (mins < 0) mins += 1440;
  mins -= breakMin;
  return Math.max(0, Math.round((mins / 60) * 100) / 100);
};

const statusStyle: Record<string, string> = {
  approved: 'bg-green-100 text-green-700',
  submitted: 'bg-amber-100 text-amber-700',
  draft: 'bg-gray-100 text-gray-600',
  rejected: 'bg-red-100 text-red-700',
};

export function EmployeeTimesheetTab({ employeeId }: EmployeeTimesheetTabProps) {
  const { toast } = useToast();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Add-entry form state
  const today = new Date().toISOString().slice(0, 10);
  const [workDate, setWorkDate] = useState(today);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [lunch, setLunch] = useState(true);
  const [notes, setNotes] = useState('');

  // Scheduled job for the picked date (home job or override) — used to pre-select
  // the first line's job so entries land on the right job without touching the dropdown.
  const [scheduledJob, setScheduledJob] = useState<{ id: number; title: string } | null>(null);
  const [autoFillTick, setAutoFillTick] = useState(0);
  const jobTouchedRef = useRef(false);

  useEffect(() => {
    if (!workDate) return;
    let cancelled = false;
    getEffectiveSchedule(workDate, workDate)
      .then(sched => {
        if (cancelled) return;
        const emp = sched.employees.find(e => e.employee_id === Number(employeeId));
        const cell = emp?.cells?.[workDate] ?? null;
        setScheduledJob(cell ? { id: cell.jobId, title: cell.jobTitle } : null);
        if (cell && !jobTouchedRef.current) {
          setLines(prev => prev.map((l, i) => (i === 0 ? { ...l, job_id: String(cell.jobId) } : l)));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [employeeId, workDate, autoFillTick]);

  const usingClock = !!(startTime && endTime);
  const dayHours = useMemo(
    () => (usingClock ? clockHours(startTime, endTime, lunch ? 30 : 0) : 0),
    [usingClock, startTime, endTime, lunch],
  );
  const lineTotal = useMemo(
    () => lines.reduce((sum, l) => sum + (parseFloat(l.hours) || 0), 0),
    [lines],
  );
  const totalHours = usingClock ? dayHours : lineTotal;

  const load = async () => {
    setLoading(true);
    try {
      setEntries(await getEmployeeTimeEntries(employeeId));
    } catch {
      toast.error('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    getJobs({ limit: 200 })
      .then(r => setJobs(r.jobs))
      .catch(() => {});
  }, []);

  const jobTitle = (id?: number | null) => jobs.find(j => j.id === id)?.title;

  const resetForm = () => {
    setWorkDate(today);
    setStartTime('');
    setEndTime('');
    setLines([blankLine()]);
    setLunch(true);
    setNotes('');
    jobTouchedRef.current = false;
    setAutoFillTick(t => t + 1); // re-apply the scheduled-job pre-select for the fresh form
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workDate) { toast.error('Pick a work date'); return; }
    const validLines = lines.filter(l => parseFloat(l.hours) > 0);
    if (!usingClock && validLines.length === 0) {
      toast.error('Enter start & end time, or hours for at least one line'); return;
    }
    if (usingClock && dayHours <= 0) { toast.error('End time must be after start time'); return; }

    // With clock times, hours come from the span; allocate to job lines (default
    // the whole day to the first line's job when no explicit split is given).
    let outLines = validLines;
    if (usingClock && validLines.length === 0) {
      outLines = [{ job_id: lines[0].job_id, description: lines[0].description, hours: String(dayHours) }];
    }
    setSaving(true);
    try {
      await createEmployeeTimeEntry(employeeId, {
        work_date: workDate,
        hours: usingClock ? dayHours : validLines.reduce((s, l) => s + parseFloat(l.hours), 0),
        break_minutes: lunch ? 30 : 0,
        notes: notes.trim() || undefined,
        start_time: usingClock ? startTime : undefined,
        end_time: usingClock ? endTime : undefined,
        job_id: outLines[0]?.job_id ? Number(outLines[0].job_id) : null,
        lines: outLines.map(l => ({
          job_id: l.job_id ? Number(l.job_id) : null,
          description: l.description.trim() || undefined,
          hours: parseFloat(l.hours) || dayHours,
        })),
      });
      toast.success('Time entry added & approved');
      resetForm();
      load();
    } catch {
      toast.error('Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await deleteAdminTimeEntry(id);
      setEntries(prev => prev.filter(en => en.id !== id));
      toast.success('Time entry deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const updateLine = (idx: number, patch: Partial<LineDraft>) =>
    setLines(prev => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  return (
    <div className="pt-4 space-y-6">
      {/* Add time entry */}
      <form onSubmit={handleSubmit} className="border border-gray-200 rounded-xl p-4 bg-gray-50/60">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-900">Add time</h4>
          <span className="text-xs text-gray-500">Admin entries are auto-approved</span>
        </div>

        <div className="flex flex-wrap items-end gap-4 mb-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Work date</span>
            <input
              type="date"
              value={workDate}
              max={today}
              onChange={e => setWorkDate(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-300 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Start</span>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">End</span>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
          </label>
          <label className="flex items-center gap-2 h-9">
            <input type="checkbox" checked={lunch} onChange={e => setLunch(e.target.checked)} />
            <span className="text-sm text-gray-700">Deduct 30-min lunch</span>
          </label>
          <div className="ml-auto text-sm text-gray-600">
            {usingClock
              ? <>Worked: <span className="font-bold text-gray-900">{dayHours.toFixed(2)} h</span></>
              : <>Total: <span className="font-bold text-gray-900">{totalHours.toFixed(2)} h</span></>}
          </div>
        </div>
        {usingClock && (
          <p className="-mt-2 mb-2 text-xs text-gray-400">Hours are calculated from start/end{lunch ? ' minus lunch' : ''}. Add job lines below to split across jobs (optional).</p>
        )}

        {scheduledJob && lines[0]?.job_id === String(scheduledJob.id) && (
          <p className="mb-1 text-xs text-gray-400">
            <span className="material-symbols-outlined text-xs align-middle mr-0.5">event_available</span>
            Scheduled on <span className="font-medium text-gray-500">{scheduledJob.title}</span> for this date — job pre-selected.
          </p>
        )}
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select
                value={l.job_id}
                onChange={e => { if (i === 0) jobTouchedRef.current = true; updateLine(i, { job_id: e.target.value }); }}
                className="h-9 px-2 rounded-lg border border-gray-300 text-sm min-w-[10rem] flex-1"
              >
                <option value="">No specific job</option>
                {scheduledJob && !jobs.some(j => j.id === scheduledJob.id) && (
                  <option value={scheduledJob.id}>{scheduledJob.title}</option>
                )}
                {jobs.map(j => (
                  <option key={j.id} value={j.id}>{j.title}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Description (optional)"
                value={l.description}
                onChange={e => updateLine(i, { description: e.target.value })}
                className="h-9 px-2 rounded-lg border border-gray-300 text-sm flex-1 min-w-[8rem]"
              />
              <input
                type="number"
                step="0.25"
                min="0"
                placeholder="Hrs"
                value={l.hours}
                onChange={e => updateLine(i, { hours: e.target.value })}
                className="h-9 px-2 rounded-lg border border-gray-300 text-sm w-20"
              />
              {lines.length > 1 && (
                <button
                  type="button"
                  onClick={() => setLines(prev => prev.filter((_, idx) => idx !== i))}
                  className="h-9 w-9 flex items-center justify-center text-gray-400 hover:text-red-600"
                  title="Remove line"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setLines(prev => [...prev, blankLine()])}
          className="mt-2 inline-flex items-center gap-1 text-sm text-primary font-medium hover:underline"
        >
          <span className="material-symbols-outlined text-base">add</span>
          Add another job line
        </button>

        <textarea
          placeholder="Notes (optional)"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          className="mt-3 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
        />

        <div className="mt-3 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
          >
            <span className={`material-symbols-outlined text-base ${saving ? 'animate-spin' : ''}`}>
              {saving ? 'progress_activity' : 'check'}
            </span>
            {saving ? 'Saving…' : 'Add time entry'}
          </button>
        </div>
      </form>

      {/* Existing entries */}
      {loading ? (
        <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
      ) : entries.length === 0 ? (
        <p className="text-gray-500 text-sm">No time entries logged yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="pb-2 font-medium">Date</th>
                <th className="pb-2 font-medium">Job</th>
                <th className="pb-2 font-medium text-right">Hours</th>
                <th className="pb-2 font-medium text-right">Break</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium">Source</th>
                <th className="pb-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {entries.map(en => {
                const lineLabel = en.lines && en.lines.length > 0
                  ? en.lines.map(l => l.job_title || jobTitle(l.job_id) || l.description || '—').join(', ')
                  : (en.job_title || jobTitle(en.job_id) || '—');
                return (
                  <tr key={en.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 whitespace-nowrap">
                      {new Date(String(en.work_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {en.start_time && en.end_time && (
                        <span className="block text-xs text-gray-400">{String(en.start_time).slice(0, 5)}–{String(en.end_time).slice(0, 5)}</span>
                      )}
                    </td>
                    <td className="py-2 max-w-[16rem] truncate" title={lineLabel}>{lineLabel}</td>
                    <td className="py-2 text-right font-semibold">{Number(en.hours).toFixed(2)}</td>
                    <td className="py-2 text-right text-gray-500">{en.break_minutes ? `${en.break_minutes}m` : '—'}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusStyle[en.status] || 'bg-gray-100 text-gray-600'}`}>
                        {en.status}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-gray-500 capitalize">{en.source}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => handleDelete(en.id)}
                        className="text-gray-400 hover:text-red-600"
                        title="Delete entry"
                      >
                        <span className="material-symbols-outlined text-base">delete</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
